import type { NovelItem } from '@app/_proto/Protos/novels';
import { logSelectSeries } from '@app/analytics';
import { ReactComponent as GoodIcon } from '@app/assets/good.svg';
import PlaceholderComponent from '@app/components/placeholder';
import { HomeEvents } from '@app/domains/home/analytics/posthog/events';
import HeroTextPlaceholder from '@app/domains/novel/components/HeroTextPlaceholder';
import NovelBlurImg from '@app/domains/novel/components/NovelBlurImage';
import { BASE_SKELETON_PROPS } from '@app/domains/novel/constants';
import { formatRatingForDisplay } from '@app/utils/utils';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

type Props = {
    content: NovelItem | undefined;
    rank: number;
    isLoading?: boolean;
};

export default function HomeTrendingContent({ content, rank, isLoading }: Props) {
    const handleClickSeries = (novel: NovelItem | undefined) => {
        if (!novel) return;

        logSelectSeries(novel);
    };

    return (
        <div className="flex py-20 gap-x-8">
            <div>
                <PlaceholderComponent
                    item={!isLoading}
                    className={'rounded-5 bg-[#e9e9e9] dark:bg-[#2a2a2a]'}
                    skeletonProps={BASE_SKELETON_PROPS}
                >
                    <Link
                        onClick={() => handleClickSeries(content)}
                        to={getNovelPath(content?.slug || '')}
                        className="relative"
                        data-posthog-click-event={HomeEvents.ClickCollectionItem}
                    >
                        <div className="h-48 w-36 sm:h-64 sm:w-48">
                            <NovelBlurImg
                                alt={content?.name}
                                srcWidth={48}
                                srcHeight={64}
                                src={content?.coverUrl?.value}
                                blurHash={content?.coverBlurHash?.value}
                                innerClassName="rounded-5 relative"
                            />
                        </div>
                    </Link>
                </PlaceholderComponent>
            </div>
            <HeroTextPlaceholder
                data-testid="skeleton"
                className="h-[24px] w-[24px] rounded-4"
                item={!isLoading}
                skeletonProps={BASE_SKELETON_PROPS}
            >
                <div
                    className={clsx(
                        'text-white font-set-r16 !font-sans p-4 h-24 w-24 rounded-4 flex justify-center items-center',
                        {
                            'bg-blue-600': rank === 1,
                            'bg-blue-500': rank === 2,
                            'bg-blue-300': rank === 3,
                            'bg-gray-500': rank >= 4,
                        }
                    )}
                >
                    {rank}
                </div>
            </HeroTextPlaceholder>
            <div className="flex-1">
                <HeroTextPlaceholder
                    data-testid="skeleton"
                    className="h-[18px] w-[180px]"
                    item={!isLoading}
                    skeletonProps={BASE_SKELETON_PROPS}
                >
                    <Link
                        onClick={() => handleClickSeries(content)}
                        to={getNovelPath(content?.slug || '')}
                        data-posthog-click-event={HomeEvents.ClickCollectionItem}
                        className="line-clamp-2 break-word font-set-sb16 text-black dark:text-white flex-1"
                    >
                        {content?.name}
                    </Link>
                </HeroTextPlaceholder>
                <HeroTextPlaceholder
                    data-testid="skeleton"
                    className="h-[12px] w-[100px] mt-5"
                    item={!isLoading}
                    skeletonProps={BASE_SKELETON_PROPS}
                >
                    <div className="line-clamp-1 break-word font-set-r12 text-grey-750 dark:text-grey-400 mt-5">
                        {content?.chapterInfo?.chapterCount?.value ?? 0} Chapters available
                    </div>
                </HeroTextPlaceholder>
                <HeroTextPlaceholder
                    data-testid="skeleton"
                    className="h-[12px] w-[30px] mt-8"
                    item={!isLoading}
                    skeletonProps={BASE_SKELETON_PROPS}
                >
                    {content?.reviewInfo?.rating !== undefined && (
                        <div className="flex items-center space-x-2 mt-8">
                            <GoodIcon className="w-12 h-12" />
                            <span className="font-set-b13">{formatRatingForDisplay(content.reviewInfo.rating)}</span>
                        </div>
                    )}
                </HeroTextPlaceholder>
            </div>
        </div>
    );
}

export const getNovelPath = (novelSlug: string) => {
    return `/novel/${novelSlug}`;
};
